import { auth } from '../../firebase';
import api from '../../api';
import { ATLAS_PLAYWRIGHT_AUTH_DISABLED, getPlaywrightIdToken } from '../../devAuth';

const ATLAS_AGENT_API_URL = process.env.REACT_APP_ATLAS_AGENT_API_URL;

let cachedIdToken = null;

export async function getIdToken() {
  if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
    cachedIdToken = getPlaywrightIdToken();
    return cachedIdToken;
  }
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  cachedIdToken = await user.getIdToken();
  return cachedIdToken;
}

/** Last session token, for requests that cannot await (map tile fetches). */
export function getCachedIdToken() {
  return cachedIdToken;
}

/** Terrain-RGB tiles from USGS 3DEP via the dev API (1 m lidar terrain). */
export function terrainTileUrlTemplate() {
  return ATLAS_AGENT_API_URL ? `${ATLAS_AGENT_API_URL}/v1/dev/terrain-tiles/{z}/{x}/{y}.png` : null;
}

export function isAtlasApiUrl(url) {
  return Boolean(ATLAS_AGENT_API_URL) && typeof url === 'string' && url.startsWith(ATLAS_AGENT_API_URL);
}

function triggerDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

function appendWorkspaceScopeParams(url, workspaceContext = {}) {
  const scopeType = workspaceContext?.scopeType || workspaceContext?.workspace_scope_type;
  const companyId = workspaceContext?.companyId || workspaceContext?.company_id;
  if (scopeType === 'company' && companyId) {
    url.searchParams.set('scope_type', 'company');
    url.searchParams.set('company_id', String(companyId));
  }
}

function appendConversationScopeParams(url, workspaceContext = {}) {
  if (workspaceContext?.scopeType === 'company' && workspaceContext?.companyId) {
    url.searchParams.set('scope_type', 'company');
    url.searchParams.set('company_id', String(workspaceContext.companyId));
  } else {
    url.searchParams.set('scope_type', 'user');
  }
}

export function applyWorkspaceScopeToPayload(payload, workspaceContext = {}) {
  if (workspaceContext?.scopeType === 'company' && workspaceContext?.companyId) {
    payload.workspace_scope_type = 'company';
    payload.company_id = Number(workspaceContext.companyId);
  }
  return payload;
}

/**
 * An Error for a failed response that keeps what callers branch on: the HTTP
 * status, the parsed body (e.g. a 409's existing_folder_id) and the API's
 * error code when it sends one.
 */
async function apiError(res, fallbackMessage) {
  const body = await res.json().catch(() => ({}));
  const detail = typeof body?.detail === 'string' ? body.detail : '';
  const message = detail || (typeof body?.error === 'string' ? body.error : '') || fallbackMessage;
  const err = new Error(message);
  err.status = res.status;
  err.body = body || {};
  err.code = typeof body?.code === 'string' ? body.code : null;
  return err;
}

export async function uploadFile({ conversationId, file, actAsUserId, workspaceContext }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId) throw new Error('Conversation ID required for file upload');

  const idToken = await getIdToken();
  const formData = new FormData();
  formData.append('file', file);

  const uploadUrl = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/upload-file`);
  if (actAsUserId) uploadUrl.searchParams.set('act_as_user_id', String(actAsUserId));
  appendWorkspaceScopeParams(uploadUrl, workspaceContext);

  const res = await fetch(uploadUrl.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
    body: formData,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Upload failed: ${res.status}`);
  }

  return res.json();
}

const AUDIO_EXTENSIONS = { 'audio/webm': 'webm', 'audio/mp4': 'mp4', 'audio/ogg': 'ogg', 'audio/mpeg': 'mp3', 'audio/wav': 'wav' };

/**
 * Composer voice input: one recorded clip -> its transcript. The conversation
 * id (when the chat already exists) lets the backend log the cost against it.
 */
export async function transcribeAudio({ blob, conversationId, durationMs }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const mime = (blob.type || 'audio/webm').split(';')[0];
  const formData = new FormData();
  formData.append('file', blob, `recording.${AUDIO_EXTENSIONS[mime] || 'webm'}`);
  if (conversationId && !String(conversationId).startsWith('pending_')) {
    formData.append('conversation_id', conversationId);
  }
  if (durationMs) formData.append('duration_ms', String(Math.round(durationMs)));

  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/transcribe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
    body: formData,
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || errData.detail || `Transcription failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchViz({ conversationId, name, ownerUserId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId) throw new Error('Missing conversationId');
  const idToken = await getIdToken();
  const url = new URL(
    `${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/viz/${encodeURIComponent(name)}`
  );
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const err = new Error(`viz fetch failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function fetchXlsxRange({ xlsxId, sheet, range, ownerUserId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/xlsx/${encodeURIComponent(xlsxId)}/range`);
  url.searchParams.set('sheet', sheet);
  url.searchParams.set('range', range);
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`xlsx range fetch failed: ${res.status}`);
  return res.json();
}

export async function downloadXlsx({ xlsxId, filename, ownerUserId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const downloadUrl = new URL(`${ATLAS_AGENT_API_URL}/v1/xlsx/${encodeURIComponent(xlsxId)}/download`);
  if (ownerUserId) downloadUrl.searchParams.set('owner_user_id', String(ownerUserId));
  const res = await fetch(downloadUrl.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`xlsx download failed: ${res.status}`);
  const blob = await res.blob();
  triggerDownload(blob, filename || `${xlsxId}.xlsx`);
}

/**
 * Bytes of a stored file. variant 'original' is the file itself; 'preview' is
 * the backend's PDF rendition of a Word/PowerPoint/spreadsheet file (built on
 * first open, then cached). Errors carry the HTTP status and the API's error
 * code (preview_too_large, preview_failed, ...).
 */
export async function fetchSavedFileBlob({ fileId, variant = 'original', ownerUserId, dataUserId, workspaceContext, signal }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const route = variant === 'preview' ? 'preview' : 'download';
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/saved-files/${encodeURIComponent(fileId)}/${route}`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body?.detail || `File ${route} failed: ${res.status}`);
    err.status = res.status;
    err.code = body?.error || null;
    throw err;
  }
  return res.blob();
}

export async function downloadSavedFile({ fileId, filename, ownerUserId, dataUserId, workspaceContext }) {
  const blob = await fetchSavedFileBlob({ fileId, ownerUserId, dataUserId, workspaceContext });
  triggerDownload(blob, filename || `${fileId}`);
}

/** Save bytes the viewer already holds, without fetching them again. */
export function saveBlobAs(blob, filename) {
  triggerDownload(blob, filename || 'download');
}

export async function fetchSavedFilePreviewUrl({ fileId, ownerUserId, dataUserId, workspaceContext }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const downloadUrl = new URL(`${ATLAS_AGENT_API_URL}/v1/saved-files/${encodeURIComponent(fileId)}/download`);
  appendFileOwnerParams(downloadUrl, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(downloadUrl.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`File preview failed: ${res.status}`);
  const blob = await res.blob();
  if (!String(blob.type || '').startsWith('image/')) {
    throw new Error('Returned file is not an image');
  }
  return URL.createObjectURL(blob);
}

export async function downloadUploadedFile({ conversationId, fileId, filename, ownerUserId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId) throw new Error('Conversation ID required for file download');
  const idToken = await getIdToken();
  const downloadUrl = new URL(
    `${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/uploaded-files/${encodeURIComponent(fileId)}/download`
  );
  if (ownerUserId) downloadUrl.searchParams.set('owner_user_id', String(ownerUserId));
  const res = await fetch(downloadUrl.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`File download failed: ${res.status}`);
  const blob = await res.blob();
  triggerDownload(blob, filename || 'download');
}

export async function fetchConversationSavedFiles({ conversationId, ownerUserId, idToken: tokenOverride }) {
  if (!ATLAS_AGENT_API_URL || !conversationId) return [];
  try {
    const idToken = tokenOverride || await getIdToken();
    const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/saved-files`);
    if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.files || [];
  } catch {
    return [];
  }
}

export async function fetchConversationUploadedFiles({ conversationId, ownerUserId, idToken: tokenOverride }) {
  if (!ATLAS_AGENT_API_URL || !conversationId) return [];
  try {
    const idToken = tokenOverride || await getIdToken();
    const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/uploaded-files`);
    if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.files || [];
  } catch {
    return [];
  }
}

export async function fetchConversationFiles({ conversationId, ownerUserId, idToken: tokenOverride, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL || !conversationId) return [];
  try {
    const idToken = tokenOverride || await getIdToken();
    const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/files`);
    if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
    appendConversationScopeParams(url, workspaceContext);
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data?.files || [];
  } catch {
    return [];
  }
}

export async function fetchBrief({ conversationId, briefId, ownerUserId, workspaceContext }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(
    `${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/briefs/${encodeURIComponent(briefId)}`
  );
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  appendConversationScopeParams(url, workspaceContext);
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`brief fetch failed: ${res.status}`);
  return res.json();
}

export async function publishBrief({ conversationId, briefId, actAsUserId, workspaceContext }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(
    `${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/briefs/${encodeURIComponent(briefId)}/publish`
  );
  if (actAsUserId) url.searchParams.set('act_as_user_id', String(actAsUserId));
  appendConversationScopeParams(url, workspaceContext);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`brief publish failed: ${res.status}`);
  return res.json();
}

// --- Pending actions (human sign-off gate; currently email send) ---

export async function listConversationPendingActions({ conversationId, status, kind }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/pending-actions`);
  if (status) url.searchParams.set('status', status);
  if (kind) url.searchParams.set('kind', kind);
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`pending actions list failed: ${res.status}`);
  return res.json();
}

export async function getPendingAction({ actionId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/pending-actions/${encodeURIComponent(actionId)}`);
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`pending action fetch failed: ${res.status}`);
  return res.json();
}

// A fresh sign-in link for a Connect card (Composio's links expire within minutes),
// asked for on every click of the card's button.
export async function getConnectLink({ actionId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/pending-actions/${encodeURIComponent(actionId)}/connect-link`);
  const res = await fetch(url.toString(), { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.detail || ''; } catch { /* ignore */ }
    const err = new Error(detail || `connect link failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function confirmPendingAction({ actionId, edits } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/pending-actions/${encodeURIComponent(actionId)}/confirm`);
  // edits (optional): { to:[...], cc:[...], subject, body, send_at } — the approver's tweaks
  // to recipient/subject/copy (and send time: ISO, or null to send now) made on the card.
  const hasEdits = edits && typeof edits === 'object' && Object.keys(edits).length > 0;
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(hasEdits ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(hasEdits ? { body: JSON.stringify(edits) } : {}),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.detail || ''; } catch { /* ignore */ }
    const err = new Error(detail || `pending action confirm failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Connected apps (Composio): the user's own Google Drive / Docs / Sheets and HubSpot.
async function connectedAppsRequest(path, { method = 'GET', body } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.detail || ''; } catch { /* ignore */ }
    const err = new Error(detail || `connected apps request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function fetchConnectedApps() {
  return connectedAppsRequest('/v1/connected-apps');
}

export function startConnectedAppConnect(toolkit, callbackUrl) {
  return connectedAppsRequest(`/v1/connected-apps/${encodeURIComponent(toolkit)}/connect`, {
    method: 'POST',
    body: callbackUrl ? { callback_url: callbackUrl } : {},
  });
}

export function requestConnectedApp(appName, note) {
  return connectedAppsRequest('/v1/connected-apps/requests', {
    method: 'POST',
    body: { app_name: appName, ...(note ? { note } : {}) },
  });
}

export function fetchAutoApprovals() {
  return connectedAppsRequest('/v1/connected-apps/auto-approvals');
}

export function removeAutoApproval(toolSlug) {
  return connectedAppsRequest(`/v1/connected-apps/auto-approvals/${encodeURIComponent(toolSlug)}`, { method: 'DELETE' });
}

export function disconnectConnectedApp(toolkit) {
  return connectedAppsRequest(`/v1/connected-apps/${encodeURIComponent(toolkit)}`, { method: 'DELETE' });
}

// Gmail connects through Composio's Google sign-in (Atlas). Outlook and IMAP still
// connect through realtorch-backend (Nylas), which also lists every mailbox.
export function startGmailConnect(callbackUrl) {
  return connectedAppsRequest('/v1/email/gmail/connect', {
    method: 'POST',
    body: callbackUrl ? { callback_url: callbackUrl } : {},
  });
}

// After the sign-in: records the new mailbox. {result: connected|limit|error|none, email}
export function finishGmailConnect() {
  return connectedAppsRequest('/v1/email/gmail/finish', { method: 'POST', body: {} });
}

// {handled: false} when the mailbox isn't a Composio Gmail: disconnect it through Nylas instead.
export function disconnectEmailAccount(accountId) {
  return connectedAppsRequest(`/v1/email/accounts/${encodeURIComponent(accountId)}/disconnect`, { method: 'POST', body: {} });
}

// Stop an approved email that is scheduled (send_at) and hasn't gone out yet.
export async function unschedulePendingAction({ actionId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/pending-actions/${encodeURIComponent(actionId)}/unschedule`);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.detail || ''; } catch { /* ignore */ }
    throw new Error(detail || `unschedule failed: ${res.status}`);
  }
  return res.json();
}

// Save the approver's edits on an email card without sending it. Returns
// { action_id, status: 'pending', preview } with the saved version.
export async function saveEmailEdits({ actionId, edits }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/pending-actions/${encodeURIComponent(actionId)}/edits`);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(edits || {}),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.detail || ''; } catch { /* not JSON */ }
    throw new Error(detail || `Could not save the edits (${res.status}).`);
  }
  return res.json();
}

export async function cancelPendingAction({ actionId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/pending-actions/${encodeURIComponent(actionId)}/cancel`);
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`pending action cancel failed: ${res.status}`);
  return res.json();
}

export async function fetchConversationBriefs({ conversationId, ownerUserId, idToken: tokenOverride, workspaceContext }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = tokenOverride || await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/briefs`);
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  appendConversationScopeParams(url, workspaceContext);
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`briefs fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchAndCacheAllBriefs({ conversationId, ownerUserId, cacheRef, workspaceContext }) {
  if (!ATLAS_AGENT_API_URL) return { briefs: [] };
  try {
    const idToken = await getIdToken();
    const listUrl = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/briefs`);
    if (ownerUserId) listUrl.searchParams.set('owner_user_id', String(ownerUserId));
    appendConversationScopeParams(listUrl, workspaceContext);
    const listRes = await fetch(listUrl.toString(), {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!listRes.ok) return { briefs: [] };
    const listData = await listRes.json();
    const briefSummaries = listData?.briefs || [];

    const fullBriefs = await Promise.all(
      briefSummaries.map(async (summary) => {
        try {
          const briefUrl = new URL(
            `${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/briefs/${encodeURIComponent(summary.brief_id)}`
          );
          if (ownerUserId) briefUrl.searchParams.set('owner_user_id', String(ownerUserId));
          appendConversationScopeParams(briefUrl, workspaceContext);
          const briefRes = await fetch(briefUrl.toString(), {
            method: 'GET',
            headers: { Authorization: `Bearer ${idToken}` },
          });
          if (briefRes.ok) {
            const briefData = await briefRes.json();
            if (briefData?.brief) {
              if (cacheRef?.current) {
                cacheRef.current.set(summary.brief_id, briefData.brief);
              }
              return { ...summary, _fullBrief: briefData.brief };
            }
          }
        } catch {
          return summary;
        }
        return summary;
      })
    );

    return { briefs: briefSummaries, fullBriefs };
  } catch {
    return { briefs: [] };
  }
}

export async function checkStreamStatus({ conversationId, messageId }) {
  if (!ATLAS_AGENT_API_URL || !conversationId || !messageId) return null;
  try {
    const idToken = await getIdToken();
    const res = await fetch(
      `${ATLAS_AGENT_API_URL}/v1/chat/stream/${encodeURIComponent(conversationId)}/${encodeURIComponent(messageId)}/status`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function findActiveStream({ conversationId, ownerUserId }) {
  if (!ATLAS_AGENT_API_URL || !conversationId) return null;
  try {
    const idToken = await getIdToken();
    const url = new URL(`${ATLAS_AGENT_API_URL}/v1/chat/stream/${encodeURIComponent(conversationId)}/active`);
    if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.active_stream || null;
  } catch {
    return null;
  }
}

export async function openResumeStream({ conversationId, messageId, lastRedisId, signal }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(
    `${ATLAS_AGENT_API_URL}/v1/chat/stream/${encodeURIComponent(conversationId)}/${encodeURIComponent(messageId)}`
  );
  url.searchParams.set('last_id', lastRedisId || '0');
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
    signal,
  });
  if (!res.ok) throw new Error(`resume stream failed: ${res.status}`);
  return res;
}

/**
 * An image as an object URL. conversationId (the chat the image is shown in)
 * lets someone who can read that chat but doesn't own it - an admin opening a
 * customer's chat link, a company teammate - load its brief images too.
 */
export async function fetchAtlasImage(imageId, { conversationId } = {}) {
  if (!imageId || !ATLAS_AGENT_API_URL) throw new Error('Image ID or API URL missing');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/images/${encodeURIComponent(imageId)}`);
  if (conversationId) url.searchParams.set('conversation_id', String(conversationId));
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`Failed to load image: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function cancelStream({ conversationId, messageId }) {
  if (!ATLAS_AGENT_API_URL || !conversationId || !messageId) return;
  const idToken = await getIdToken();
  await fetch(
    `${ATLAS_AGENT_API_URL}/v1/chat/stream/${encodeURIComponent(conversationId)}/${encodeURIComponent(messageId)}/cancel`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
    }
  );
}

export async function rollbackConversation({ conversationId, messageId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId || !messageId) throw new Error('conversationId and messageId required');
  const idToken = await getIdToken();
  const res = await fetch(
    `${ATLAS_AGENT_API_URL}/v1/admin/conversations/${encodeURIComponent(conversationId)}/rollback`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ message_id: messageId }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.detail || err.error || 'Rollback failed');
  }
  return res.json();
}

export async function fetchConversationToolTraces({ conversationId, messageId, ownerUserId, dataUserId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId) throw new Error('conversationId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/admin/conversations/${encodeURIComponent(conversationId)}/tool-traces`);
  if (messageId) url.searchParams.set('message_id', String(messageId));
  appendFileOwnerParams(url, { ownerUserId, dataUserId });
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Tool trace fetch failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchConversationSnapshot({ conversationId, ownerUserId, dataUserId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId) throw new Error('conversationId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/admin/conversations/${encodeURIComponent(conversationId)}/snapshot`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId });
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Snapshot fetch failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchConversationRuntimeDebug({ conversationId, ownerUserId, dataUserId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId) throw new Error('conversationId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/admin/conversations/${encodeURIComponent(conversationId)}/runtime-debug`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId });
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Runtime debug fetch failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchSharedBrief(shareId) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/share/briefs/${encodeURIComponent(shareId)}`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error(`share fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchSharedBriefImage(shareId, imageId) {
  if (!ATLAS_AGENT_API_URL || !shareId || !imageId) {
    throw new Error('Missing required parameters');
  }
  const url = `${ATLAS_AGENT_API_URL}/v1/share/briefs/${encodeURIComponent(shareId)}/images/${encodeURIComponent(imageId)}`;
  const res = await fetch(url, { method: 'GET' });
  if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext } = {}) {
  if (dataUserId) {
    url.searchParams.set('data_user_id', String(dataUserId));
  } else if (ownerUserId) {
    url.searchParams.set('owner_user_id', String(ownerUserId));
  }
  appendWorkspaceScopeParams(url, workspaceContext);
}

export async function fetchVfsTree({ ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/tree`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw await apiError(res, `VFS tree fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchVfsFolderContents({ folderId, ownerUserId, dataUserId, workspaceContext, limit, offset } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/folders/${encodeURIComponent(folderId)}`);
  if (limit != null) url.searchParams.set('limit', String(limit));
  if (offset != null) url.searchParams.set('offset', String(offset));
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw await apiError(res, `VFS folder fetch failed: ${res.status}`);
  return res.json();
}

// --- File library dashboard (/v1/vfs/files, /stats, /versions, folder merge) ---

async function vfsGet(path, { params, ownerUserId, dataUserId, workspaceContext, signal, fallbackMessage } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}${path}`);
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === false) return;
    url.searchParams.set(key, String(value));
  });
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
    signal,
  });
  if (!res.ok) throw await apiError(res, `${fallbackMessage || 'Request failed'}: ${res.status}`);
  return res.json();
}

/**
 * Dashboard listing. q searches filename, summaries, tags, category and key
 * entities (all words must match). Returns { files: [FileRow], total, offset, limit }.
 */
export function fetchVfsFiles({
  q, sourceGroup, kind, folderId, unfiled, sort, limit, offset,
  ownerUserId, dataUserId, workspaceContext, signal,
} = {}) {
  return vfsGet('/v1/vfs/files', {
    params: {
      q: q ? String(q).trim() : '',
      source_group: sourceGroup && sourceGroup !== 'all' ? sourceGroup : '',
      kind: kind && kind !== 'all' ? kind : '',
      folder_id: folderId || '',
      unfiled: unfiled ? 'true' : '',
      sort: sort && sort !== 'recent' ? sort : '',
      limit: limit != null ? limit : '',
      offset: offset ? offset : '',
    },
    ownerUserId, dataUserId, workspaceContext, signal,
    fallbackMessage: 'File listing failed',
  });
}

/** { total_files, total_bytes, unfiled_count, folder_count, by_source_group, by_kind } */
export function fetchVfsStats({ ownerUserId, dataUserId, workspaceContext, signal } = {}) {
  return vfsGet('/v1/vfs/stats', { ownerUserId, dataUserId, workspaceContext, signal, fallbackMessage: 'File stats failed' });
}

/** { root_file_id, versions: [{ file_id, version_number, filename, size_bytes, created_at, is_current_version }] }, newest first. */
export function fetchVfsFileVersions({ fileId, ownerUserId, dataUserId, workspaceContext, signal } = {}) {
  if (!fileId) return Promise.reject(new Error('fileId required'));
  return vfsGet(`/v1/vfs/files/${encodeURIComponent(fileId)}/versions`, {
    ownerUserId, dataUserId, workspaceContext, signal, fallbackMessage: 'Version history failed',
  });
}

/** Summary, tags, key entities, folders (with path), source and conversation of one file. */
export function fetchVfsFileMetadata({ fileId, ownerUserId, dataUserId, workspaceContext, signal } = {}) {
  if (!fileId) return Promise.reject(new Error('fileId required'));
  return vfsGet(`/v1/vfs/files/${encodeURIComponent(fileId)}/metadata`, {
    ownerUserId, dataUserId, workspaceContext, signal, fallbackMessage: 'File details failed',
  });
}

/**
 * Merge one folder into another: its files and subfolders move into the target
 * (same-named subfolders merge), then the source folder is removed.
 * Returns { ok, target_folder_id, moved_files, moved_folders }.
 */
export async function mergeVfsFolder({ folderId, targetFolderId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!folderId || !targetFolderId) throw new Error('folderId and targetFolderId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/folders/${encodeURIComponent(folderId)}/merge`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_folder_id: targetFolderId }),
  });
  if (!res.ok) throw await apiError(res, `Merge folders failed: ${res.status}`);
  return res.json();
}

// --- Folder suggestions for unfiled files ---

// A /v1/vfs call with the workspace params (any method; GET sends no body).
// Errors keep status, code and body (apiError).
async function vfsSend(path, { method = 'POST', body, ownerUserId, dataUserId, workspaceContext, fallbackMessage } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}${path}`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const headers = { Authorization: `Bearer ${idToken}` };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await apiError(res, `${fallbackMessage || 'Request failed'}: ${res.status}`);
  return res.json();
}

/**
 * Ask Atlas to suggest folders for unfiled files. Without fileIds the server
 * takes the newest unfiled files that have no suggestion yet (max 40). Each
 * call is a model request. Returns { ok, attempted, suggested }.
 */
export function requestVfsFolderSuggestions({ fileIds, force, ownerUserId, dataUserId, workspaceContext } = {}) {
  const body = {};
  if (Array.isArray(fileIds) && fileIds.length) body.file_ids = fileIds;
  if (force) body.force = true;
  return vfsSend('/v1/vfs/suggestions', {
    body, ownerUserId, dataUserId, workspaceContext, fallbackMessage: 'Folder suggestions failed',
  });
}

/**
 * Move a file into its suggested folder (creating the suggested new folders).
 * Returns { ok, file_id, folders: [{ folder_id, name, path }] }. A 409 carries
 * err.code "no_suggestion" or "suggested_folder_gone".
 */
export function acceptVfsFolderSuggestion({ fileId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!fileId) return Promise.reject(new Error('fileId required'));
  return vfsSend(`/v1/vfs/files/${encodeURIComponent(fileId)}/suggestion/accept`, {
    ownerUserId, dataUserId, workspaceContext, fallbackMessage: 'Move to the suggested folder failed',
  });
}

/** Dismiss a file's folder suggestion (it is not suggested again for a day). Returns { ok, file_id }. */
export function dismissVfsFolderSuggestion({ fileId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!fileId) return Promise.reject(new Error('fileId required'));
  return vfsSend(`/v1/vfs/files/${encodeURIComponent(fileId)}/suggestion`, {
    method: 'DELETE', ownerUserId, dataUserId, workspaceContext, fallbackMessage: 'Dismiss suggestion failed',
  });
}

// --- Filing preferences: free-text filing rules for one library ---

/**
 * The library's filing preferences, which auto-sort, folder suggestions and
 * Atlas in chat follow. Returns { preferences, max_chars, updated_at,
 * updated_by_user_id, scope, company_id, editable }. 404 on an older backend.
 */
export function fetchFilingPreferences({ ownerUserId, dataUserId, workspaceContext } = {}) {
  return vfsSend('/v1/vfs/filing-preferences', {
    method: 'GET', ownerUserId, dataUserId, workspaceContext, fallbackMessage: 'Filing preferences failed to load',
  });
}

/**
 * Replace the library's filing preferences ("" clears them). Returns the same
 * shape as fetchFilingPreferences. Over the limit: 400 with err.code "too_long".
 */
export function saveFilingPreferences({ preferences, ownerUserId, dataUserId, workspaceContext } = {}) {
  return vfsSend('/v1/vfs/filing-preferences', {
    method: 'PUT',
    body: { preferences: typeof preferences === 'string' ? preferences : '' },
    ownerUserId,
    dataUserId,
    workspaceContext,
    fallbackMessage: 'Saving filing preferences failed',
  });
}

export async function searchConversations({ query, ownerUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/search`);
  url.searchParams.set('q', query);
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  appendConversationScopeParams(url, workspaceContext);
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  return res.json();
}

/**
 * Rename and/or pin a conversation. Pass either or both of `title` / `pinned`.
 *
 * A title set here is marked custom server-side, which stops the Haiku
 * auto-titler from overwriting it later.
 */
export async function updateConversation({ conversationId, title, pinned, ownerUserId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}`);
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));

  const body = {};
  if (title !== undefined) body.title = title;
  if (pinned !== undefined) body.pinned = pinned;

  const res = await fetch(url.toString(), {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Conversation update failed: ${res.status}`);
  return res.json();
}

/**
 * apiError, plus: these endpoints send their code as `error`
 * ({error: "not_starter"}), so it becomes err.code when no `code` came back.
 */
async function conversationApiError(res, fallbackMessage) {
  const err = await apiError(res, fallbackMessage);
  if (!err.code && typeof err.body?.error === 'string' && /^[a-z0-9_]+$/.test(err.body.error)) {
    err.code = err.body.error;
  }
  return err;
}

/**
 * What deleting a chat would remove, and whether this user may delete it:
 * { conversation_id, title, scope_type, company_id, can_delete, reason, counts }.
 * 404 conversation_not_found when it is missing or already deleted.
 */
export async function fetchConversationDeletion(conversationId) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId) throw new Error('conversationId required');
  const idToken = await getIdToken();
  const res = await fetch(
    `${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/deletion`,
    { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } },
  );
  if (!res.ok) throw await conversationApiError(res, `Loading the chat's contents failed: ${res.status}`);
  return res.json();
}

/**
 * Permanently delete a chat and everything tied to it. keepFiles keeps its
 * files (a company chat's files move to the caller's personal library).
 * 403 not_starter when the caller did not start it; 404 when already gone.
 */
export async function deleteConversation({ conversationId, keepFiles = false } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId) throw new Error('conversationId required');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ keep_files: Boolean(keepFiles) }),
  });
  if (!res.ok) throw await conversationApiError(res, `Delete chat failed: ${res.status}`);
  return res.json();
}

export async function generateConversationTitle({ conversationId, message, filenames, ownerUserId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const body = { message };
  if (filenames && filenames.length > 0) body.filenames = filenames;
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/generate-title`);
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Title generation failed: ${res.status}`);
  return res.json();
}

export async function fetchRecentFiles({ type = 'all', limit = 20, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/files/recent`);
  url.searchParams.set('type', type);
  url.searchParams.set('limit', String(limit));
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw await apiError(res, `Recent files fetch failed: ${res.status}`);
  return res.json();
}

export async function deleteVfsFile({ fileId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!fileId) throw new Error('fileId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/files/${encodeURIComponent(fileId)}`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw await apiError(res, `Delete failed: ${res.status}`);
  return res.json();
}

export async function deleteVfsFolder({ folderId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!folderId) throw new Error('folderId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/folders/${encodeURIComponent(folderId)}`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw await apiError(res, `Delete folder failed: ${res.status}`);
  return res.json();
}

export async function renameVfsFile({ fileId, filename, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!fileId || !filename) throw new Error('fileId and filename required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/files/${encodeURIComponent(fileId)}/rename`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename }),
  });
  if (!res.ok) throw await apiError(res, `Rename failed: ${res.status}`);
  return res.json();
}

export async function renameVfsFolder({ folderId, name, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!folderId || !name) throw new Error('folderId and name required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/folders/${encodeURIComponent(folderId)}`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw await apiError(res, `Rename folder failed: ${res.status}`);
  return res.json();
}

export async function createVfsFolder({ name, parentFolderId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!name) throw new Error('name required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/folders`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const body = { name };
  if (parentFolderId) body.parent_folder_id = parentFolderId;
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, `Create folder failed: ${res.status}`);
  return res.json();
}

/**
 * Move a file into `folderId` (null = unfile it). Without `sourceFolderId` the
 * backend takes the file out of every folder it is in first, so this is a real
 * move, not a copy. Only pass `sourceFolderId` when the caller truly knows the
 * one folder link to replace.
 * Returns { ok, folders: [{ folder_id, name, path }] }.
 */
export async function moveVfsFile({ fileId, folderId = null, sourceFolderId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!fileId) throw new Error('fileId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/files/${encodeURIComponent(fileId)}/move`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const body = { folder_id: folderId || null };
  if (sourceFolderId) body.source_folder_id = sourceFolderId;
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, `Move file failed: ${res.status}`);
  return res.json();
}

export async function moveVfsFolder({ folderId, parentFolderId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!folderId) throw new Error('folderId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/folders/${encodeURIComponent(folderId)}`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ parent_folder_id: parentFolderId || null }),
  });
  if (!res.ok) throw await apiError(res, `Move folder failed: ${res.status}`);
  return res.json();
}

export async function fetchVfsProcessingStatus({ fileId, ownerUserId, dataUserId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!fileId) throw new Error('fileId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/files/${encodeURIComponent(fileId)}/processing-status`);
  appendFileOwnerParams(url, { ownerUserId, dataUserId, workspaceContext });
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Processing status fetch failed: ${res.status}`);
  }
  return res.json();
}

export async function shareFileToCompany({ fileId, companyId, ownerUserId, dataUserId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!fileId || !companyId) throw new Error('fileId and companyId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/files/${encodeURIComponent(fileId)}/share-to-company`);
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  if (dataUserId) url.searchParams.set('data_user_id', String(dataUserId));
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ company_id: Number(companyId) }),
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Send to company failed: ${res.status}`);
  }
  return res.json();
}

export async function shareFolderToCompany({ folderId, companyId, ownerUserId, dataUserId, targetParentFolderId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!folderId || !companyId) throw new Error('folderId and companyId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/vfs/folders/${encodeURIComponent(folderId)}/share-to-company`);
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  if (dataUserId) url.searchParams.set('data_user_id', String(dataUserId));
  const body = { company_id: Number(companyId) };
  if (targetParentFolderId) body.target_parent_folder_id = targetParentFolderId;
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Send folder to company failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchCompanyProfile({ companyId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!companyId) throw new Error('companyId required');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company/${encodeURIComponent(companyId)}/profile`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`Company profile fetch failed: ${res.status}`);
  return res.json();
}

export async function updateCompanyDescription({ companyId, description } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!companyId) throw new Error('companyId required');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company/${encodeURIComponent(companyId)}/profile/description`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ description }),
  });
  if (!res.ok) throw new Error(`Company description save failed: ${res.status}`);
  return res.json();
}

/** Turn automatic filing of new files on or off for a company library. */
/**
 * Turn auto-sort of new files on or off for the workspace's library, personal
 * or company (PUT /v1/vfs/auto-sort). Returns { ok, scope, company_id, auto_sort }.
 */
export function setLibraryAutoSort({ autoSort, ownerUserId, dataUserId, workspaceContext } = {}) {
  return vfsSend('/v1/vfs/auto-sort', {
    method: 'PUT',
    body: { auto_sort: Boolean(autoSort) },
    ownerUserId,
    dataUserId,
    workspaceContext,
    fallbackMessage: 'Auto-sort change failed',
  });
}

export async function setCompanyAutoSort({ companyId, autoSort } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!companyId) throw new Error('companyId required');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company/${encodeURIComponent(companyId)}/auto-sort`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ auto_sort: Boolean(autoSort) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Auto-sort change failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchAtlasUserPreferences() {
  const res = await api.get('/api/user/preferences');
  return res.data;
}

export async function updateAtlasUserPreferences(preferences = {}) {
  const res = await api.put('/api/user/preferences', preferences);
  return res.data;
}

export async function fetchAtlasMcpConnector() {
  const res = await api.get('/api/user/connectors/mcp');
  return res.data;
}

export async function updateAtlasMcpConnector(connector = {}) {
  const res = await api.put('/api/user/connectors/mcp', connector);
  return res.data;
}

export async function fetchCompanyActivity({ companyId, limit = 200, beforeActivityId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!companyId) throw new Error('companyId required');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/company/${encodeURIComponent(companyId)}/activity`);
  url.searchParams.set('limit', String(limit));
  if (beforeActivityId) url.searchParams.set('before_activity_id', String(beforeActivityId));
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`Company activity fetch failed: ${res.status}`);
  return res.json();
}

export async function uploadCompanyLogo({ companyId, file } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!companyId || !file) throw new Error('companyId and file required');
  const idToken = await getIdToken();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company/${encodeURIComponent(companyId)}/logo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
    body: formData,
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Logo upload failed: ${res.status}`);
  }
  return res.json();
}

export async function addCompanyMemory({ companyId, memory } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!companyId || !memory) throw new Error('companyId and memory required');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company/${encodeURIComponent(companyId)}/memories`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ memory }),
  });
  if (!res.ok) throw new Error(`Company memory add failed: ${res.status}`);
  return res.json();
}

export async function updateCompanyMemory({ companyId, memoryId, memory } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!companyId || !memoryId || !memory) throw new Error('companyId, memoryId, and memory required');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company/${encodeURIComponent(companyId)}/memories/${encodeURIComponent(memoryId)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ memory }),
  });
  if (!res.ok) throw new Error(`Company memory update failed: ${res.status}`);
  return res.json();
}

export async function deleteCompanyMemory({ companyId, memoryId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!companyId || !memoryId) throw new Error('companyId and memoryId required');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company/${encodeURIComponent(companyId)}/memories/${encodeURIComponent(memoryId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`Company memory delete failed: ${res.status}`);
  return res.json();
}

// Personal profile + memories. `ownerUserId` lets an admin act on a client's profile.
function personalProfileUrl(path, ownerUserId) {
  const url = new URL(`${ATLAS_AGENT_API_URL}${path}`);
  if (ownerUserId) url.searchParams.set('owner_user_id', String(ownerUserId));
  return url.toString();
}

export async function fetchBuilderProfile({ ownerUserId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const res = await fetch(personalProfileUrl('/v1/builder-profile', ownerUserId), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`Failed to load profile: ${res.status}`);
  return res.json();
}

export async function addUserMemory({ memory, type, detail, ownerUserId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!memory || !type) throw new Error('memory and type required');
  const idToken = await getIdToken();
  const body = { memory, type };
  if (detail) body.detail = detail;
  const res = await fetch(personalProfileUrl('/v1/memories', ownerUserId), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Memory add failed: ${res.status}`);
  return res.json();
}

// The backend may supersede the row on edit, so the returned memory can carry a new id.
export async function updateUserMemory({ memoryId, memory, detail, ownerUserId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!memoryId || !memory) throw new Error('memoryId and memory required');
  const idToken = await getIdToken();
  const body = { memory };
  if (detail !== undefined) body.detail = detail;
  const res = await fetch(personalProfileUrl(`/v1/memories/${encodeURIComponent(memoryId)}`, ownerUserId), {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Memory update failed: ${res.status}`);
  return res.json();
}

export async function deleteUserMemory({ memoryId, ownerUserId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!memoryId) throw new Error('memoryId required');
  const idToken = await getIdToken();
  const res = await fetch(personalProfileUrl(`/v1/memories/${encodeURIComponent(memoryId)}`, ownerUserId), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error('Failed to delete memory');
  return { ok: true };
}

/**
 * Link a library file to a conversation. Company files must say so
 * (workspace_scope_type + company_id) or the backend resolves the file in the
 * personal library and answers 404.
 */
export async function attachFileToConversation({ conversationId, fileId, workspaceContext } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  if (!conversationId || !fileId) throw new Error('conversationId and fileId required');
  const idToken = await getIdToken();
  const body = applyWorkspaceScopeToPayload({ file_id: fileId }, workspaceContext || {});
  const res = await fetch(
    `${ATLAS_AGENT_API_URL}/v1/conversations/${encodeURIComponent(conversationId)}/attach-file`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw await apiError(res, `Attach file failed: ${res.status}`);
  return res.json();
}

export async function fetchCompanyUsageBilling(periodOffset = 0, ownerUserId = null) {
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing`);
  url.searchParams.set('period_offset', String(periodOffset));
  if (ownerUserId != null) url.searchParams.set('owner_user_id', String(ownerUserId));
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`usage-billing fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchCompanyCreditBilling(ownerUserId = null, monthOffset = 0) {
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/credits`);
  if (ownerUserId != null) url.searchParams.set('owner_user_id', String(ownerUserId));
  if (monthOffset) url.searchParams.set('month_offset', String(monthOffset));
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`credit-billing fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchUsageLimitStatus(workspaceContext = {}) {
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/usage-limit`);
  if (workspaceContext?.isCompanyScope && workspaceContext?.companyId) {
    url.searchParams.set('company_id', String(workspaceContext.companyId));
  }
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`Usage limit check failed: ${res.status}`);
  return res.json();
}

export async function fetchMemberUsageConversations({ userId, offset = 0, limit = 10, periodOffset = 0, ownerUserId = null }) {
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/members/${userId}/conversations`);
  url.searchParams.set('offset', String(offset));
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('period_offset', String(periodOffset));
  if (ownerUserId != null) url.searchParams.set('owner_user_id', String(ownerUserId));
  const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) throw new Error(`member conversations failed: ${res.status}`);
  return res.json();
}

export async function setUsageOverageCap(capUsd) {
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/overage-cap`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ cap_usd: capUsd }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.reason || `set cap failed: ${res.status}`);
    error.reason = body.reason;
    throw error;
  }
  return body;
}

export async function updateUsageBillingMemberRole({ userId, role }) {
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/members/${userId}/role`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.reason || `update member role failed: ${res.status}`);
    error.reason = body.reason;
    throw error;
  }
  return body;
}

export async function setUsageAlertPreference({ enabled, thresholdPct }) {
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/alert-preference`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled: Boolean(enabled), threshold_pct: Number(thresholdPct) }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.reason || `set usage alert failed: ${res.status}`);
    error.reason = body.reason;
    throw error;
  }
  return body;
}

// Owner/admin opt-in to top-up emails (the CRM sends them): every $100, $200,
// $500 or $1,000 of $100 top-ups in a billing period.
export async function setTopupEmailPreference({ enabled, everyUsd }) {
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/topup-email`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled: Boolean(enabled), every_usd: Number(everyUsd) }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.reason || `set top-up email failed: ${res.status}`);
    error.reason = body.reason;
    throw error;
  }
  return body;
}

export async function approveUsageBillingConsent() {
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/consent`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.reason || `approve consent failed: ${res.status}`);
    error.reason = body.reason;
    throw error;
  }
  return body;
}

export async function revokeUsageBillingConsent() {
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/company-usage-billing/consent`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.reason || `revoke consent failed: ${res.status}`);
    error.reason = body.reason;
    throw error;
  }
  return body;
}

// ---------------------------------------------------------------------------
// Live Web Research runs
// ---------------------------------------------------------------------------

export async function createResearchRun({ objective, name, startUrl, conversationId, useResidentialProxy }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const payload = { objective };
  if (name) payload.name = name;
  if (startUrl) payload.start_url = startUrl;
  if (conversationId) payload.conversation_id = conversationId;
  if (useResidentialProxy !== undefined) payload.use_residential_proxy = !!useResidentialProxy;
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/research/runs`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.ok === false) {
    throw new Error(body.error || `start research run failed: ${res.status}`);
  }
  return body;
}

export async function listResearchRuns({ limit = 50, conversationId } = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/research/runs`);
  url.searchParams.set('limit', String(limit));
  if (conversationId) url.searchParams.set('conversation_id', conversationId);
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`list research runs failed: ${res.status}`);
  const body = await res.json();
  return body.runs || [];
}

export async function fetchResearchRun({ runId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/research/runs/${encodeURIComponent(runId)}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`fetch research run failed: ${res.status}`);
  return res.json();
}

export async function openResearchRunStream({ runId, lastId, signal }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const url = new URL(`${ATLAS_AGENT_API_URL}/v1/research/runs/${encodeURIComponent(runId)}/stream`);
  url.searchParams.set('last_id', lastId || '0');
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
    signal,
  });
  if (!res.ok) throw new Error(`research run stream failed: ${res.status}`);
  return res;
}

export async function cancelResearchRun({ runId }) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const idToken = await getIdToken();
  const res = await fetch(`${ATLAS_AGENT_API_URL}/v1/research/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`cancel research run failed: ${res.status}`);
  return res.json();
}

// ── Land Evaluation (property-context document for signed-in users) ─────────

export class LandEvaluationError extends Error {
  constructor(message, { status, code, fieldErrors, retryAfter } = {}) {
    super(message);
    this.name = 'LandEvaluationError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors || [];
    this.retryAfter = retryAfter;
  }
}

/**
 * POST /v1/land-evaluation — one address (or coordinate) in, the dev
 * property-context document out (production datasets + nearby_places); see
 * atlas-backend/docs/property-context-dev-api.md.
 *
 * Tree canopy (property-context 2.2.0): the tool always asks for the canopy
 * geometry so the map can draw it; `canopy_geojson` requires `canopy_radius_ft`
 * (50–1000 ft), which also enables `summary.surround`. Older backends ignore
 * both options and simply omit `tree_canopy` from the document.
 *
 * Building footprints (property-context 2.2.0): `footprints_radius_ft` (50–1000)
 * adds `summary.surround`; `footprints_geojson` (requires the radius) adds the
 * neighbouring outlines and is only sent when the caller asks for it. The
 * on-parcel outlines always ride in `buildings[]`. Older backends ignore both
 * options and omit `building_footprints`.
 */
export async function evaluateLand({
  address,
  coordinates,
  demographicsRadiusMiles = 5,
  housingHistoryMonths = 96,
  placesRadiusMiles = 3,
  compsRadiusMiles = 2,
  canopyGeojson = true,
  canopyRadiusFt = 330,
  footprintsRadiusFt = 200,
  footprintsGeojson = false,
  floodRadiusFt = null,
  signal,
} = {}) {
  if (!ATLAS_AGENT_API_URL) throw new Error('REACT_APP_ATLAS_AGENT_API_URL is not set');
  const hasAddress = typeof address === 'string' && address.trim().length > 0;
  const hasCoordinates = coordinates
    && Number.isFinite(Number(coordinates.latitude))
    && Number.isFinite(Number(coordinates.longitude));
  if (hasAddress === hasCoordinates) {
    throw new LandEvaluationError('Provide either an address or a coordinate pair', { code: 'INVALID_REQUEST' });
  }

  const idToken = await getIdToken();
  const payload = {
    location: hasAddress
      ? { address: address.trim() }
      : { coordinates: { latitude: Number(coordinates.latitude), longitude: Number(coordinates.longitude) } },
    options: {
      demographics_radius_miles: Number(demographicsRadiusMiles) || 5,
      housing_history_months: Number(housingHistoryMonths) || 96,
      places_radius_miles: Number(placesRadiusMiles) || 3,
      comps_radius_miles: Number(compsRadiusMiles) || 2,
      canopy_geojson: Boolean(canopyGeojson),
      canopy_radius_ft: Math.min(1000, Math.max(50, Number(canopyRadiusFt) || 330)),
      ...(Number.isFinite(Number(footprintsRadiusFt)) && Number(footprintsRadiusFt) > 0
        ? { footprints_radius_ft: Math.min(1000, Math.max(50, Math.round(Number(footprintsRadiusFt)))) }
        : {}),
      ...(footprintsGeojson && Number.isFinite(Number(footprintsRadiusFt)) && Number(footprintsRadiusFt) > 0 ? { footprints_geojson: true } : {}),
      // Flood zones on the lot always come back with geometry; a radius adds the
      // ring's numbers (`surround`) and, for the map, the zones around the lot.
      // No radius sends neither option (older backends reject unknown options).
      ...(Number(floodRadiusFt) > 0
        ? { flood_radius_ft: Math.min(1000, Math.max(50, Math.round(Number(floodRadiusFt)))), flood_geojson: true }
        : {}),
    },
  };

  let res;
  try {
    res = await fetch(`${ATLAS_AGENT_API_URL}/v1/land-evaluation`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new LandEvaluationError('Could not reach the evaluation service. Check your connection and try again.', { code: 'NETWORK' });
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = data?.error || {};
    const retryAfter = Number(res.headers.get('Retry-After')) || undefined;
    const fallback = {
      401: 'Your session has expired. Sign in again to continue.',
      403: 'Land Evaluation is not enabled for this account.',
      422: 'That location could not be resolved. Try a fuller address (street, city, state, ZIP).',
      429: 'Too many evaluations in the last minute. Give it a moment and try again.',
      503: 'The evaluation service is not available right now.',
    }[res.status] || `Evaluation failed (${res.status}).`;
    throw new LandEvaluationError(error.message || data?.detail || fallback, {
      status: res.status,
      code: error.code || (res.status === 429 ? 'RATE_LIMITED' : undefined),
      fieldErrors: error.field_errors,
      retryAfter,
    });
  }
  return data;
}
